import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type PaginationType from "@/types/serializers/Pagination";

interface PaginationProps {
  pagination: PaginationType;
  className?: string;
}

function handlePageChange(page: number) {
  router.get(
    window.location.pathname,
    { page },
    { preserveScroll: true, preserveState: true },
  );
}

export function Pagination({ pagination, className }: PaginationProps) {
  const {
    current_page,
    total_pages,
    total_count,
    next_page,
    previous_page,
    range_start,
    range_end,
  } = pagination;

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const showEllipsis = total_pages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (current_page > 3) {
      pages.push("ellipsis");
    }

    const start = Math.max(2, current_page - 1);
    const end = Math.min(total_pages - 1, current_page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current_page < total_pages - 2) {
      pages.push("ellipsis");
    }

    if (total_pages > 1) {
      pages.push(total_pages);
    }

    return pages;
  };

  if (total_pages <= 1) {
    return null;
  }

  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div className="text-muted-foreground text-sm">
        Page {current_page} of {total_pages} ({range_start}-{range_end} of{" "}
        {total_count})
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => previous_page && handlePageChange(previous_page)}
          disabled={!previous_page}
        >
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="text-muted-foreground px-2"
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={current_page === page ? "default" : "outline"}
                size="sm"
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => next_page && handlePageChange(next_page)}
          disabled={!next_page}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
